import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, Unlock } from 'lucide-react';
import toast from 'react-hot-toast';
import { getModuleIcon } from '@/pages/ModuleTemplatesPage/iconHelper';
import { cx } from '../cx';
import type { JourneyNavItem } from './navConfig';
import { bottomTabItems } from './navConfig';

interface JourneyNavProps {
  items: JourneyNavItem[];
  /** Called after a nav click — used to close the mobile drawer. */
  onNavigate?: () => void;
  isSkillProfileLocked?: boolean;
}

const isActive = (pathname: string, route: string) => {
  if (route === '/student/journey') return pathname === route || pathname.startsWith('/student/journey');
  if (route.startsWith('/student/code')) return pathname.startsWith('/student/code');
  return pathname === route || pathname.startsWith(route + '/');
};

const I18N_KEY: Record<string, string> = {
  journey: 'nav.journey', calendar: 'nav.calendar', coding: 'nav.coding', 'coding-tasks': 'nav.coding',
  lab: 'nav.lab', 'python-lab': 'nav.lab', achievements: 'nav.achievements',
  'skill-profile': 'nav.skillProfile', profile: 'nav.profile',
};

const useNavLabel = () => {
  const { t } = useTranslation('journey');
  return (item: JourneyNavItem) => (I18N_KEY[item.key] ? t(I18N_KEY[item.key], item.label) : item.label);
};

/** Vertical list used by the desktop rail and the mobile/tablet drawer. */
export const JourneyNav: React.FC<JourneyNavProps> = ({ items, onNavigate, isSkillProfileLocked }) => {
  const { t } = useTranslation('journey');
  const label = useNavLabel();
  const navigate = useNavigate();

  return (
    <nav className="sp-nav" aria-label={t('nav.section', 'My Fellowship')}>
      <div className="sp-nav__section">{t('nav.section', 'My Fellowship')}</div>
      {items.map((item) => {
        const Icon = getModuleIcon(item.icon);
        const isLocked = item.key === 'skill-profile' && isSkillProfileLocked;

        const handleClick = (e: React.MouseEvent) => {
          if (isLocked) {
            e.preventDefault();
            toast.error('Please complete your profile section to 100% after that unlock', {
              id: 'skill-profile-locked',
              duration: 4500,
              icon: '🔒',
            });
            navigate('/student/profile');
            if (onNavigate) onNavigate();
            return;
          }
          if (onNavigate) onNavigate();
        };

        return (
          <NavLink
            key={item.key}
            to={isLocked ? '/student/profile' : item.route}
            onClick={handleClick}
            className={({ isActive: active }) =>
              cx('sp-nav__item', (active || undefined) && 'is-active', item.soon && 'is-soon')
            }
          >
            {({ isActive: active }) => (
              <>
                {active && <span className="sp-nav__item-rail-mark" aria-hidden="true" />}
                <Icon size={18} />
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{label(item)}</span>
                {item.soon && <span className="sp-nav__soon">{t('nav.soon', 'Soon')}</span>}
                {item.key === 'skill-profile' && (
                  isLocked ? (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        padding: '2px 6px',
                        borderRadius: 4,
                        background: 'rgba(245, 158, 11, 0.15)',
                        color: '#d97706',
                        border: '1px solid rgba(245, 158, 11, 0.25)',
                        flexShrink: 0,
                      }}
                    >
                      <Lock size={10} />
                      <span>Locked</span>
                    </span>
                  ) : (
                    <span
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        fontSize: 10,
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        padding: '2px 6px',
                        borderRadius: 4,
                        background: 'rgba(16, 185, 129, 0.15)',
                        color: '#059669',
                        border: '1px solid rgba(16, 185, 129, 0.25)',
                        flexShrink: 0,
                      }}
                    >
                      <Unlock size={10} />
                      <span>Unlocked</span>
                    </span>
                  )
                )}
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
};

/** Fixed 5-slot bar shown under 768px. */
export const JourneyBottomNav: React.FC<{ items: JourneyNavItem[]; pathname: string; isSkillProfileLocked?: boolean }> = ({ items, pathname, isSkillProfileLocked }) => {
  const { t } = useTranslation('journey');
  const label = useNavLabel();
  const navigate = useNavigate();

  return (
    <div className="sp-bottomnav" role="navigation" aria-label={t('nav.section', 'Primary')}>
      {bottomTabItems(items).map((item) => {
        const Icon = getModuleIcon(item.icon);
        const active = isActive(pathname, item.route);
        const isLocked = item.key === 'skill-profile' && isSkillProfileLocked;

        const handleClick = (e: React.MouseEvent) => {
          if (isLocked) {
            e.preventDefault();
            toast.error('Please complete your profile section to 100% after that unlock', {
              id: 'skill-profile-locked',
              duration: 4500,
              icon: '🔒',
            });
            navigate('/student/profile');
            return;
          }
        };

        return (
          <NavLink
            key={item.key}
            to={isLocked ? '/student/profile' : item.route}
            onClick={handleClick}
            aria-current={active ? 'page' : undefined}
            className={cx('sp-bottomnav__item', active && 'is-active')}
          >
            <Icon size={20} />
            <span>{label(item)}</span>
          </NavLink>
        );
      })}
    </div>
  );
};
